import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Shield, CheckCircle2, ArrowRight } from 'lucide-react';
interface CreatorWebsitesProps {
  setActiveSection: (val: string) => void;
}
export function CreatorWebsites({
  setActiveSection
}: CreatorWebsitesProps) {
  return <section className="pt-48 pb-32 px-6 max-w-7xl mx-auto" data-id="element-163">
      <div className="text-left mb-16" data-id="element-164">
        <h2 className="font-['Archivo_Black'] text-4xl md:text-6xl uppercase mb-4 bg-gradient-to-r from-[#FFD700] to-[#FF0000] bg-clip-text text-transparent" data-id="element-165">
          Creator Websites
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl" data-id="element-166">
          High-performance, SEO-optimized sites built for the entertainment
          industry.
        </p>
      </div>

      {/* Immersive Gallery Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16" data-id="element-167">
        {[{
        name: 'DJ',
        color: 'from-red-600/20 to-yellow-500/5',
        glow: 'rgba(255, 0, 0, 0.2)',
        image: 'https://images.unsplash.com/photo-1571266028243-3716f02d2d2e?auto=format&fit=crop&q=80&w=800'
      }, {
        name: 'Artist',
        color: 'from-yellow-500/20 to-red-600/5',
        glow: 'rgba(255, 215, 0, 0.2)',
        image: 'https://images.unsplash.com/photo-1514525253361-bee8718a74a2?auto=format&fit=crop&q=80&w=800'
      }, {
        name: 'Influencer',
        color: 'from-red-600/10 to-yellow-500/20',
        glow: 'rgba(255, 215, 0, 0.2)',
        image: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&q=80&w=800'
      }].map((archetype, i) => <motion.div key={i} whileHover={{
        y: -10
      }} className={`aspect-square rounded-2xl border border-white/10 bg-gradient-to-br ${archetype.color} backdrop-blur-xl relative overflow-hidden flex items-end p-8 group cursor-pointer hover:border-[#FFD700]/30 transition-all`} data-id="element-168">
            <img src={archetype.image} alt={archetype.name} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-110 transition-all duration-700 mix-blend-overlay" data-id="element-169" />
            <div className="absolute inset-0 pointer-events-none" style={{
          background: `radial-gradient(circle at 30% 30%, ${archetype.glow} 0%, transparent 60%)`
        }} data-id="element-170" />
            <span className="font-['Archivo_Black'] text-2xl uppercase tracking-wider relative z-10 group-hover:text-[#FFD700] transition-colors" data-id="element-171">
              {archetype.name}
            </span>
          </motion.div>)}
      </div>

      {/* Feature Spotlight Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16" data-id="element-172">
        {[{
        title: 'Custom Animations',
        desc: 'Smooth, branded transitions and effects that captivate your audience.',
        icon: '🎬'
      }, {
        title: 'High-Res Media Kits',
        desc: 'Professional press materials and high-fidelity assets ready for industry download.',
        icon: '📊'
      }, {
        title: 'Social Feed Aggregation',
        desc: 'Sync and display all your social platforms in one centralized, beautiful hub.',
        icon: '📱'
      }, {
        title: 'Lightning Fast',
        desc: 'Optimized for extreme speed and top-tier SEO ranking on global search engines.',
        icon: '⚡'
      }].map((feature, i) => <div key={i} className="p-8 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-md flex items-start gap-6 hover:bg-white/[0.07] transition-colors" data-id="element-173">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FFD700] to-[#FF0000] flex items-center justify-center text-2xl shrink-0 shadow-lg" data-id="element-174">
              {feature.icon}
            </div>
            <div data-id="element-175">
              <h4 className="font-['Archivo_Black'] text-sm uppercase tracking-wider mb-2" data-id="element-176">
                {feature.title}
              </h4>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-177">
                {feature.desc}
              </p>
            </div>
          </div>)}
      </div>

      {/* Feature Spotlight Section */}
      <div className="p-10 bg-gradient-to-br from-[#FFD700]/10 to-[#FF0000]/10 border border-[#FFD700]/20 rounded-3xl backdrop-blur-xl" data-id="element-178">
        <div className="flex items-center gap-3 text-[#FFD700] font-['Archivo_Black'] text-lg uppercase tracking-widest mb-8" data-id="element-179">
          <span className="text-2xl" data-id="element-180">✦</span> Feature Spotlight
        </div>
        <div className="flex flex-wrap gap-4 mb-10" data-id="element-181">
          {['Custom Domains', 'Analytics Dashboard', 'Email Integration', 'Global CDN', 'DDoS Protection'].map((badge, i) => <span key={i} className="px-6 py-2 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-full text-xs font-bold text-[#FFD700] uppercase tracking-widest" data-id="element-182">
              {badge}
            </span>)}
        </div>
        <div className="p-8 bg-red-600/5 border border-red-600/30 rounded-2xl mb-12" data-id="element-183">
          <h5 className="font-['Archivo_Black'] text-sm uppercase tracking-wider text-red-500 mb-3" data-id="element-184">
            Custom Domains
          </h5>
          <p className="text-sm text-gray-300 leading-relaxed" data-id="element-185">
            Full ownership of your URL. No subdomains, no third-party branding.
            Your brand, your rules, your empire.
          </p>
        </div>

        {/* CTA to Hosting */}
        <div className="flex flex-col items-center text-center py-12 border-t border-white/5" data-id="element-186">
          <h3 className="font-['Archivo_Black'] text-2xl md:text-3xl uppercase mb-6" data-id="element-187">
            Ready to host your empire?
          </h3>
          <p className="text-gray-400 mb-10 max-w-xl" data-id="element-188">
            Experience lightning-fast speeds and 99.9% uptime with our
            enterprise-grade Niishcloud hosting infrastructure.
          </p>
          <button onClick={() => setActiveSection('hosting')} className="group flex items-center gap-3 bg-[#FFD700] text-black px-10 py-5 rounded-full font-bold uppercase tracking-widest hover:bg-white transition-all shadow-[0_0_30px_rgba(255,215,0,0.2)]" data-id="element-189">
            Explore Pro Hosting
            <ArrowRight className="group-hover:translate-x-2 transition-transform" data-id="element-190" />
          </button>
        </div>
      </div>
    </section>;
}